<template>
  <div class="recharge-records">
    <div class="page-header">
      <h2>充值记录</h2>
      <p>查看所有充值记录信息</p>
    </div>
    
    <div class="page-content">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">
              {{ store.storeName }}
            </option>
          </select>
          <input
            v-model="searchCustomerName"
            type="text"
            placeholder="请输入客户姓名"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <input
            v-model="searchPhone"
            type="text"
            placeholder="请输入手机号"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <input
            v-model="searchStartTime"
            type="date"
            class="date-input"
            @change="handleSearch"
          />
          <input
            v-model="searchEndTime"
            type="date"
            class="date-input"
            @change="handleSearch"
          />
          <button @click="handleSearch" class="search-btn">搜索</button>
          <button @click="handleReset" class="reset-btn">重置</button>
          <button @click="handleExport" class="export-btn" :disabled="loading">导出</button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-header">
          <h3>充值记录列表</h3>
        </div>
        
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>客户ID</th>
                <th>客户姓名</th>
                <th>手机号</th>
                <th>门店名称</th>
                <th>充值金额</th>
                <th>支付方式</th>
                <th>充值时间</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="8" class="loading-cell">
                  <div class="loading-spinner">
                    <div class="spinner"></div>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!rechargeList || rechargeList.length === 0" class="empty-row">
                <td colspan="8" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-else v-for="record in rechargeList" :key="record.id" class="data-row">
                <td>{{ record.customerId }}</td>
                <td>{{ record.customerName || '-' }}</td>
                <td>{{ record.customerPhone || '-' }}</td>
                <td>{{ record.storeName || '-' }}</td>
                <td class="amount-cell">{{ record.amount.toFixed(2) }}元</td>
                <td>{{ record.paymentMethod || '-' }}</td>
                <td>{{ formatDate(record.rechargeTime) }}</td>
                <td>{{ record.remark || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页区域 -->
        <div class="pagination-section" v-if="!loading && rechargeList && rechargeList.length > 0">
          <div class="pagination-info">
            共 {{ pagination.totalElements }} 条记录，第 {{ pagination.number + 1 }} / {{ pagination.totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button 
              @click="handlePageChange(0)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              首页
            </button>
            <button 
              @click="handlePageChange(pagination.number - 1)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              上一页
            </button>
            <span class="page-info">
              {{ pagination.number + 1 }} / {{ pagination.totalPages }}
            </span>
            <button 
              @click="handlePageChange(pagination.number + 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              下一页
            </button>
            <button 
              @click="handlePageChange(pagination.totalPages - 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              末页
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'RechargeRecords',
  data() {
    return {
      rechargeList: [],
      storeList: [],
      customerList: [],
      loading: false,
      searchStoreId: '',
      searchCustomerName: '',
      searchPhone: '',
      searchStartTime: '',
      searchEndTime: '',
      pagination: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      }
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadRechargeList()
  },
  methods: {
    async loadStoreList() {
      try {
        const response = await request.get('/api/stores/getStores', {
          params: {
            page: 0,
            size: 1000 // 获取所有门店用于下拉框
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.storeList = response.data.data.content || []
        }
      } catch (error) {
        console.error('获取门店列表失败:', error)
      }
    },

    async loadRechargeList() {
      this.loading = true
      try {
        const response = await request.post(`/api/recharge/query?page=${this.pagination.number}&size=${this.pagination.size}`, {
          storeId: this.searchStoreId || null,
          customerName: this.searchCustomerName || null,
          phone: this.searchPhone || null,
          startTime: this.searchStartTime || null,
          endTime: this.searchEndTime || null
        })

        if (response.data.success && response.data.code === 200) {
          this.rechargeList = response.data.data.content || []
          this.pagination = {
            number: response.data.data.number || 0,
            size: response.data.data.size || 20,
            totalElements: response.data.data.totalElements || 0,
            totalPages: response.data.data.totalPages || 0,
            first: response.data.data.first || true,
            last: response.data.data.last || true
          }
        } else {
          throw new Error(response.data.message || '获取充值记录失败')
        }
      } catch (error) {
        console.error('获取充值记录失败:', error)
        let errorMessage = '获取充值记录失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '获取充值记录失败，请重试'
        }
        
        alert(errorMessage)
        this.rechargeList = []
        // 重置分页信息
        this.pagination = {
          number: 0,
          size: 20,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true
        }
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.number = 0 // 重置到第一页
      this.loadRechargeList()
    },

    handleReset() {
      this.searchStoreId = ''
      this.searchCustomerName = ''
      this.searchPhone = ''
      this.searchStartTime = ''
      this.searchEndTime = ''
      this.pagination.number = 0
      this.loadRechargeList()
    },

    handlePageChange(pageNumber) {
      if (pageNumber >= 0 && pageNumber < this.pagination.totalPages) {
        this.pagination.number = pageNumber
        this.loadRechargeList()
      }
    },

    async handleExport() {
      try {
        const exportParams = {
          storeId: this.searchStoreId || null,
          customerName: this.searchCustomerName || null,
          phone: this.searchPhone || null,
          startTime: this.searchStartTime || null,
          endTime: this.searchEndTime || null
        }

        const response = await request.post('/api/recharge/export', exportParams, {
          responseType: 'blob' // 重要：设置响应类型为blob以处理文件下载
        })

        // 创建下载链接
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // 生成文件名（包含时间戳）
        const now = new Date()
        const timestamp = now.toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')
        link.download = `充值记录_${timestamp}.xlsx`
        
        // 触发下载
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 清理URL对象
        window.URL.revokeObjectURL(url)
        
      } catch (error) {
        console.error('导出失败:', error)
        let errorMessage = '导出失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '导出失败，请重试'
        }
        
        alert(errorMessage)
      }
    },

    getPaymentMethodName(paymentMethod) {
      const methodMap = {
        '2': '线下扫码'
      }
      return methodMap[paymentMethod] || '线下扫码'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.recharge-records {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 搜索区域 */
.search-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.search-box {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.store-select, .search-input, .date-input {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.store-select {
  background: white;
  cursor: pointer;
  min-width: 120px;
}

.search-input {
  flex: 1;
  max-width: 150px;
}

.date-input {
  min-width: 140px;
}

.search-input:focus, .date-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.store-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.search-btn, .reset-btn, .export-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.search-btn:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

.reset-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.export-btn {
  background: #52c41a;
  color: white;
  border-color: #52c41a;
}

.export-btn:hover:not(:disabled) {
  background: #73d13d;
  border-color: #73d13d;
}

.export-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #d9d9d9;
}

/* 表格区域 */
.table-section {
  margin-bottom: 24px;
}

.table-header {
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background: #fafafa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
}

.data-row:hover {
  background: #f5f5f5;
}

.loading-cell, .empty-cell {
  text-align: center;
  color: #999;
  font-style: italic;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.amount-cell {
  color: #52c41a;
  font-weight: 600;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-badge.inactive {
  background: #fff2e8;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

/* 分页区域 */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.page-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #d9d9d9;
}

.page-info {
  padding: 6px 12px;
  color: #666;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-box {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input, .date-input, .store-select {
    max-width: none;
  }
  
  .pagination-section {
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }
  
  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
